import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { VehicleActionType } from '../../context/vehicle/actions';
import { useVehicleContext } from '../../context/vehicle/context';

import { getPublicVehicleAvatar } from '../../services/requests';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';

interface UseRetrieveVehicleAvatarProps {
  vehicleId?: string;
}

type UseRetrieveVehicleAvatarResponseProps = ServiceResponse<Function, string>;

// Hooks act as 'Adapter' layer.
const useRetrieveVehicleAvatar = ({
  vehicleId,
}: UseRetrieveVehicleAvatarProps): UseRetrieveVehicleAvatarResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: activeProject } = useRetrieveActiveProject();
  const { vehicleState, vehicleDispatch } = useVehicleContext();

  const { isLoading, isError, isSuccess, refetch } = useQuery(
    [`planner_vehicle_avatar`, vehicleId],
    async () => await getPublicVehicleAvatar({ vehicleId: vehicleId!, activeProject }),
    {
      staleTime: Infinity,
      retry: 2,
      enabled: activeProject !== undefined && vehicleId !== undefined,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        vehicleDispatch({
          type: VehicleActionType.GET_VEHICLE_AVATAR,
          payload: {
            vehicleId: vehicleId!,
            vehicleAvatar: response ?? undefined,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Retrieve Vehicle Avatar Error: `, err?.response);

        vehicleDispatch({
          type: VehicleActionType.GET_VEHICLE_AVATAR,
          payload: {
            vehicleId: vehicleId!,
            vehicleAvatar: undefined,
          },
        });

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    data: vehicleId ? vehicleState.vehiclesAvatarMap[vehicleId] : undefined,
    run: refetch,
  };
};

export default useRetrieveVehicleAvatar;
